// src/pages/users/SearchUserModalContent.tsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@/icons';
import { Pessoa, PessoaIntegration } from '../../types/usuarios/Pessoa';
import { SEARCH_USER_LOCATIONS, SEARCH_USER_PARAMETERS, SearchUserLocation, SearchUserParameter } from '../../constants';
import '../../pages/users/SearchUserModalContent.css';
import SearchButton from '../../components/common/SearchButton';
import { useApiCall } from '../../hooks/useApiCall';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface SearchUserModalContentProps {
  theme: 'dark' | 'light';
  onSearch: (result: { ativos: Pessoa[], inativos: Pessoa[], integracao: PessoaIntegration[] }) => void;
  onClose?: () => void;
}

const SearchUserModalContent: React.FC<SearchUserModalContentProps> = ({ onSearch, onClose }) => {
  const [selectedLocation, setSelectedLocation] = useState<SearchUserLocation | ''>('');
  const [selectedParameter, setSelectedParameter] = useState<SearchUserParameter | ''>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const { callApi, isLoading } = useApiCall();

  const parameterMap = {
    Nome: 'nome',
    CPF: 'cpf',
    Whatsapp: 'whatsapp',
    Email: 'email',
    UserName: 'userName',
    'Código da Loja Vinculada': 'codFilial',
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLocation(e.target.value as SearchUserLocation | '');
    setSelectedParameter('');
    setSearchValue('');
  };

  const handleParameterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedParameter(e.target.value as SearchUserParameter | '');
    setSearchValue('');
  };

  const handleSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = async () => {
    const porQualParametro = parameterMap[selectedParameter as keyof typeof parameterMap];
    if (!porQualParametro || !selectedLocation || !searchValue) {
      await showAlert('Preencha todos os campos antes de buscar!', { variant: 'warning' });
      return;
    }

    try {
      const endpoint = `/v1/busca-inteligente?ondeProcurar=${selectedLocation}&porQualParametro=${porQualParametro}&parametroProcurado=${encodeURIComponent(searchValue)}`;

      await callApi('get', endpoint, null, {
        onSuccess: (data) => {
          const ativos = data.ativos || [];
          const inativos = data.inativos || [];
          const integracao = data.integracao || [];
          const totalResults = ativos.length + inativos.length + integracao.length;

          onSearch({ ativos, inativos, integracao });

          if (totalResults === 0) {
            Toast.info('Nenhum resultado encontrado para a busca');
          } else {
            Toast.success('Pesquisa realizada com sucesso!');
          }

          if (onClose) {
            onClose();
          }
        },
      });
    } catch (err) {
      logger.error('Erro ao buscar:', err);
      await showAlert('Erro ao realizar a busca', { variant: 'error' });
    }
    // Removido finally: gerenciado pelo hook.
  };

  return (
    <div className="search-container">
      <h2><FontAwesomeIcon icon={faMagnifyingGlass} /> Procurar Usuário</h2>
      <div>
        <label className="form-label">
          Onde procurar?
          <select
            value={selectedLocation}
            onChange={handleLocationChange}
            className="form-select"
          >
            <option value="">Selecione uma opção</option>
            {SEARCH_USER_LOCATIONS.map((location: SearchUserLocation) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>
        </label>
      </div>

      {selectedLocation && (
        <div>
          <label className="form-label">
            Por qual parâmetro procurar?
            <select
              value={selectedParameter}
              onChange={handleParameterChange}
              className="form-select"
            >
              <option value="">Selecione um parâmetro</option>
              {SEARCH_USER_PARAMETERS.map((param: SearchUserParameter) => (
                <option key={param} value={param}>
                  {param}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {selectedParameter && (
        <div>
          <label className="form-label">
            Por favor digite o parâmetro procurado
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchValueChange}
              className="form-input"
              placeholder="Digite pelo menos 1 caractere"
            />
          </label>
          {searchValue.length >= 1 && (
            <SearchButton
              isLoading={isLoading}
              onClick={handleSearch}
              disabled={isLoading || searchValue.length < 1}
              className="search-modal-button"
            >
              Procurar
            </SearchButton>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchUserModalContent;